import { useEffect, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import type { FulfillmentAdminClient } from '@/infrastructure/FulfillmentAdminClient';
import { useModalDialog } from '@/pages/fulfillment/components/useModalDialog';
import { formatBulkResult, NOT_ATTEMPTED, UNCONFIRMED_BULK_RESULT } from '@/pages/fulfillment/components/bulkResult';
import type { BulkItemReason } from '@/pages/fulfillment/components/bulkResult';
import type { FulfillmentToastState } from '@/pages/fulfillment/components/useFulfillmentToast';
import { attemptFingerprint, AttemptKeys } from '@/utils/attemptKeys';
import { describeFailure } from '@/utils/fulfillmentErrors';
import { readAsCsv } from '@/utils/marketingImportFile';
import { MISSING } from '../inventoryView';
import { validateStockSku, type StockSkuLookup } from '../stockSkuValidation';
import {
  LOCATION_IMPORT_ACCEPT,
  TOO_MANY_SHELVES,
  importFileHash,
  importSkus,
  parseLocationImport,
  planLocationImport,
  readImportInventory,
  reservationsMovedText,
  samePlan,
  tallyLocationImport,
} from '../locationImport';
import type { LocationImportLine, LocationImportPlanLine } from '../locationImport';

export interface LocationImportModalProps {
  client: FulfillmentAdminClient;
  stockClient: StockSkuLookup;
  onClose: () => void;
  /** Runs once any move went through or may have, so the stock list behind the dialog is read again. */
  onApplied: () => void;
}

type Phase = 'idle' | 'reading' | 'ready' | 'applying' | 'done';

const READ_ERROR = 'Could not read the stock for these SKUs. Try again shortly.';
const SKU_LOOKUP_CONCURRENCY = 5;

const throwIfCanceled = (signal: AbortSignal) => {
  if (signal.aborted) {
    throw new DOMException('Import validation canceled', 'AbortError');
  }
};

export const STOCK_CHANGED_NOTICE =
  'The stock changed since this preview was read. Review the updated rows, then apply again.';

const itemOf = (line: LocationImportLine): string => `Row ${line.rowNo} · ${line.sku || MISSING}`;

const isUnconfirmed = (failure: unknown): boolean =>
  isAxiosError(failure) && (!failure.response || failure.response.status >= 500);

const STATUS_TEXT: Record<LocationImportPlanLine['status'], string> = {
  move: 'Will move',
  unchanged: 'No change',
  blocked: 'Not applied',
  superseded: 'Superseded',
};

const SKIPPED_REASON: Record<Exclude<LocationImportPlanLine['status'], 'move'>, string> = {
  unchanged: 'Already in place',
  blocked: 'Not applied',
  superseded: 'Replaced by a later row',
};

/**
 * Sets many shelves from one file [SSD-7138]. Every row is previewed against the shelves the server
 * holds before anything is sent. Any invalid row blocks the entire import with its reason;
 * a valid file is sent one move at a time — the same move the Location field makes.
 */
export const LocationImportModal = ({ client, stockClient, onClose, onApplied }: LocationImportModalProps) => {
  const [phase, setPhase] = useState<Phase>('idle');
  const [fileError, setFileError] = useState<string | null>(null);
  const [lines, setLines] = useState<LocationImportLine[]>([]);
  const [fileHash, setFileHash] = useState('');
  const [plan, setPlan] = useState<LocationImportPlanLine[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [results, setResults] = useState<Record<number, string>>({});
  const [summary, setSummary] = useState<FulfillmentToastState | null>(null);
  const readRef = useRef(0);
  const readControllerRef = useRef<AbortController | null>(null);
  useEffect(
    () => () => {
      readRef.current += 1;
      readControllerRef.current?.abort();
    },
    [],
  );
  const beginRead = () => {
    readControllerRef.current?.abort();
    const controller = new AbortController();
    readControllerRef.current = controller;
    return { request: ++readRef.current, signal: controller.signal };
  };
  const applyingRef = useRef(false);
  /**
   * One key per file row and ask, kept until the move is known to have landed [D15]. Applying the same
   * file again after a lost answer sends the same key, so the server replays instead of moving twice;
   * a key is dropped once its move landed, so the same file run again later moves again as asked.
   */
  const moveKeys = useRef(new AttemptKeys());
  const busy = phase === 'reading' || phase === 'applying';
  const close = () => {
    if (!applyingRef.current) {
      readRef.current += 1;
      readControllerRef.current?.abort();
      onClose();
    }
  };
  const dialog = useModalDialog(close);

  const planFor = async (fileLines: LocationImportLine[], signal: AbortSignal) => {
    throwIfCanceled(signal);
    // Validate even superseded/unchanged rows. A bad SKU must never allow a partial import.
    const errors = new Map<string, string | null>();
    const skus = [...new Set(fileLines.map((line) => line.sku))];
    for (let start = 0; start < skus.length; start += SKU_LOOKUP_CONCURRENCY) {
      throwIfCanceled(signal);
      await Promise.all(
        skus.slice(start, start + SKU_LOOKUP_CONCURRENCY).map(async (sku) => {
          errors.set(sku, await validateStockSku(stockClient, sku, signal));
        }),
      );
    }
    throwIfCanceled(signal);
    const validLines = fileLines.filter((line) => !errors.get(line.sku));
    const inventory = await readImportInventory((params) => {
      throwIfCanceled(signal);
      return client.listInventory(params, signal);
    }, importSkus(validLines));
    throwIfCanceled(signal);
    return planLocationImport(fileLines, inventory).map((line): LocationImportPlanLine => {
      const error = errors.get(line.sku);
      return error ? { ...line, status: 'blocked', reason: error } : line;
    });
  };

  const choose = async (file: File | undefined) => {
    const { request, signal } = beginRead();
    setFileError(null);
    setNotice(null);
    setPlan([]);
    setLines([]);
    setResults({});
    setSummary(null);
    if (!file) {
      setPhase('idle');
      return;
    }
    setPhase('reading');
    try {
      const text = await readAsCsv(file);
      const parsed = parseLocationImport(text);
      if (parsed.error) {
        if (request === readRef.current) {
          setFileError(parsed.error);
          setPhase('idle');
        }
        return;
      }
      const next = await planFor(parsed.lines, signal);
      if (request === readRef.current) {
        setLines(parsed.lines);
        setFileHash(importFileHash(text));
        setPlan(next);
        setPhase('ready');
      }
    } catch (failure) {
      if (request === readRef.current) {
        setFileError(
          failure instanceof Error && failure.message === TOO_MANY_SHELVES
            ? `${TOO_MANY_SHELVES} Split the file and import each part.`
            : READ_ERROR,
        );
        setPhase('idle');
      }
    }
  };

  /** Recheck the selected file after a transient catalog failure without requiring a re-upload. */
  const retryValidation = async () => {
    if (phase !== 'ready' || applyingRef.current) {
      return;
    }
    const { request, signal } = beginRead();
    setPhase('reading');
    setNotice(null);
    try {
      const next = await planFor(lines, signal);
      if (request === readRef.current) {
        setPlan(next);
      }
    } catch {
      if (request === readRef.current) {
        setNotice(READ_ERROR);
      }
    } finally {
      if (request === readRef.current) {
        setPhase('ready');
      }
    }
  };

  const apply = async () => {
    if (applyingRef.current || phase !== 'ready' || plan.some((line) => line.status === 'blocked')) {
      return;
    }
    const { signal } = beginRead();
    applyingRef.current = true;
    setPhase('applying');
    setNotice(null);
    let moved = 0;
    // A lost answer may still have moved its row, so the list behind is read again either way.
    let unconfirmed = false;
    try {
      // Another desk moves stock all day: the moves sent must be the ones the operator just read.
      let fresh: LocationImportPlanLine[];
      try {
        fresh = await planFor(lines, signal);
      } catch {
        if (signal.aborted) {
          return;
        }
        setNotice(READ_ERROR);
        setPhase('ready');
        return;
      }
      if (!samePlan(plan, fresh)) {
        setPlan(fresh);
        setNotice(STOCK_CHANGED_NOTICE);
        setPhase('ready');
        return;
      }
      if (fresh.some((line) => line.status === 'blocked')) {
        setPlan(fresh);
        setPhase('ready');
        return;
      }
      const moves = fresh.filter((line) => line.status === 'move');
      const failed: BulkItemReason[] = [];
      const notAttempted: BulkItemReason[] = [];
      const outcome: Record<number, string> = {};
      for (const [index, line] of moves.entries()) {
        if (signal.aborted) {
          return;
        }
        const fingerprint = attemptFingerprint([
          'locationImport',
          fileHash,
          line.rowNo,
          line.sku,
          line.sourceLocation,
          line.toLocation,
          line.qty,
        ]);
        try {
          const result = await client.moveStock({
            sku: line.sku,
            fromLocation: line.sourceLocation!,
            toLocation: line.toLocation,
            qty: line.qty!,
            idempotencyKey: moveKeys.current.keyFor(fingerprint),
          });
          moveKeys.current.release(fingerprint);
          moved += 1;
          // The server's own count of what went along outranks the preview's. A replay moved nothing this time and
          // reports 0, and an older server reports none, so both fall back to what the preview read.
          outcome[line.rowNo] = `Moved ${line.qty} from ${line.sourceLocation}.${reservationsMovedText(
            result.replayed ? line.reservedQty : (result.reservationsMoved ?? line.reservedQty),
          )}`;
        } catch (failure) {
          if (isUnconfirmed(failure)) {
            // A lost answer is not a refusal: this row may have moved, and nothing after it is sent.
            unconfirmed = true;
            outcome[line.rowNo] = UNCONFIRMED_BULK_RESULT;
            failed.push({ item: itemOf(line), reason: UNCONFIRMED_BULK_RESULT });
            for (const rest of moves.slice(index + 1)) {
              outcome[rest.rowNo] = NOT_ATTEMPTED;
              notAttempted.push({ item: itemOf(rest), reason: NOT_ATTEMPTED });
            }
            break;
          }
          const reason = describeFailure(failure, 'The move was refused.');
          outcome[line.rowNo] = `Refused — ${reason}`;
          failed.push({ item: itemOf(line), reason });
        }
        setResults({ ...outcome });
      }
      setResults(outcome);
      const skipped = fresh
        .filter((line) => line.status !== 'move')
        .map((line) => ({
          item: itemOf(line),
          reason: SKIPPED_REASON[line.status as keyof typeof SKIPPED_REASON],
        }));
      setSummary(formatBulkResult('Location Import', moved, [...skipped, ...notAttempted], failed));
      setPhase('done');
    } finally {
      applyingRef.current = false;
      if (moved > 0 || unconfirmed) {
        onApplied();
      }
    }
  };

  const tally = tallyLocationImport(plan);

  return (
    <dialog
      className="inv-modal loc-import-modal"
      ref={dialog}
      aria-label="Import locations"
      onClick={(event) => event.target === event.currentTarget && close()}
    >
      <header>
        Import Locations
        <button
          type="button"
          className="x"
          aria-label="Close the location import"
          disabled={phase === 'applying'}
          onClick={close}
        >
          ✕
        </button>
      </header>
      <div className="body">
        <label className="loc-import-file">
          <span>File (CSV or Excel)</span>
          <input
            type="file"
            aria-label="Location file"
            accept={LOCATION_IMPORT_ACCEPT}
            disabled={busy}
            onChange={(event) => void choose(event.target.files?.[0])}
          />
        </label>
        <div className="note">
          The first row names the columns: <b>SKU</b>, <b>Location</b>, and optionally <b>From Location</b>. Each
          SKU&apos;s stock moves to its Location; only SKUs already on a shelf are changed, and nothing is sent until
          you apply. Units reserved for orders move with the stock. Every SKU must be numeric and exist in the catalog.
          Fix all blocked rows before applying; no rows are submitted from an invalid file. When a SKU is listed twice
          for the same shelf, the last row wins.
        </div>
        {phase === 'reading' && <div className="note">Reading the file and the stock…</div>}
        {fileError && (
          <div role="alert" className="note warn">
            {fileError}
          </div>
        )}
        {notice && (
          <div role="alert" className="note warn">
            {notice}
          </div>
        )}
        {summary && (
          <div role="status" className={`note${summary.tone === 'success' ? '' : ' warn'}`}>
            {summary.text}
          </div>
        )}
        {plan.length > 0 && (
          <>
            <div className="loc-import-tally" aria-label="Import summary">
              {`${tally.move} to move · ${tally.unchanged} already in place · ${tally.blocked} not applied${
                tally.superseded > 0 ? ` · ${tally.superseded} replaced by a later row` : ''
              }`}
            </div>
            <table className="tbl loc-import-table" aria-label="Location import preview">
              {/* Laid out from these widths, so a long result or reason wraps in its own cell instead of moving the others. */}
              <colgroup>
                <col style={{ width: '7%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '43%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Row</th>
                  <th>SKU</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Qty</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {plan.map((line) => (
                  <tr
                    key={line.rowNo}
                    className={
                      line.status === 'blocked' || line.status === 'superseded' ? 'loc-import-blocked' : undefined
                    }
                  >
                    <td className="num">{line.rowNo}</td>
                    <td>{line.sku || MISSING}</td>
                    <td>{line.sourceLocation ?? (line.fromLocation || MISSING)}</td>
                    <td>{line.toLocation || MISSING}</td>
                    <td className="num">{line.qty ?? MISSING}</td>
                    <td>
                      {results[line.rowNo] ?? (
                        <>
                          <b>{STATUS_TEXT[line.status]}</b>
                          {line.reason && ` — ${line.reason}`}
                          {line.status === 'move' &&
                            line.reservedQty !== undefined &&
                            ` — ${line.reservedQty} reserved ${line.reservedQty === 1 ? 'unit moves' : 'units move'} with it`}
                          {line.replacesRows &&
                            ` · replaces row${line.replacesRows.length > 1 ? 's' : ''} ${line.replacesRows.join(', ')}`}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
      <div className="foot">
        <button type="button" className="btn btn-gray btn-sm" disabled={phase === 'applying'} onClick={close}>
          {phase === 'done' ? 'Close' : 'Cancel'}
        </button>
        {phase !== 'done' && tally.blocked > 0 && (
          <button type="button" className="btn btn-gray btn-sm" disabled={busy} onClick={() => void retryValidation()}>
            Retry validation
          </button>
        )}
        {phase !== 'done' && (
          <button
            type="button"
            className="btn btn-blue btn-sm"
            disabled={phase !== 'ready' || tally.move === 0 || tally.blocked > 0}
            onClick={() => void apply()}
          >
            {phase === 'applying' ? 'Moving…' : `Apply ${tally.move} ${tally.move === 1 ? 'move' : 'moves'}`}
          </button>
        )}
      </div>
    </dialog>
  );
};
